import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { runWithConcurrency } from '@adornis/bh-util/concurrency.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { loadRawEntityDataByIDOrFail, loadRawEntityDataByQuery } from '@adornis/bh-util/util.js';
import {
  getProductPriceForUser,
  moveUpFromWaitinglistIntoParticipating,
} from '@adornis/buchungsheld-db/api/participation.js';
import { calculateWorkshopLeftCapacity } from '@adornis/buchungsheld-db/api/workshop.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { ParticipationWorkshop } from '@adornis/buchungsheld-db/entities/ParticipationWorkshop.js';
import { getMergedProductSettings, Setting } from '@adornis/buchungsheld-db/entities/settings.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { getDbPluginForScope } from '@adornis/buchungsheld-db/db-plugins.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { getNachruecker } from '@adornis/buchungsheld-db/logic/participation.js';
import { notifyNachrueckplatzVerfuegbar } from '@adornis/buchungsheld-db/notification/nachrueckplatz-verfuegbar.js';
import { notifyWartelisteVorrueck } from '@adornis/buchungsheld-db/notification/warteliste-vorrueck.js';
import { Subject } from 'rxjs';
import { bufferTime, filter, map } from 'rxjs/operators';

export type WartelistenUpdate = Record<
  string,
  {
    place?: { old: Maybe<number>; now: number };
    canManuallyNachruecken: boolean;
    canAutoNachruecken: boolean;
  }
>;

type _NL_PARTICIPATION = NarrowedEntityData<
  Participation,
  {
    _id: 1;
    userID: 1;
    state: 1;
    waitingListPlace: 1;
    nachrueckPriority: 1;
    createdAt: 1;
  }
> & {
  productPriceForUser?: Maybe<{ unitAmount: number }>;
};
export function waitinglistSortLogic(a: _NL_PARTICIPATION, b: _NL_PARTICIPATION) {
  const aPriority = a.nachrueckPriority || '';
  const bPriority = b.nachrueckPriority || '';

  if (aPriority !== bPriority) {
    if (aPriority === 'entry_requirements_not_met') return 1;
    if (bPriority === 'entry_requirements_not_met') return -1;
    if (aPriority === 'high') return -1;
    if (bPriority === 'high') return 1;
    throw new Error('unexpected nachrueckPriority: ' + aPriority + ' ' + bPriority);
  }

  return a.createdAt.getTime() - b.createdAt.getTime();
}

// this function takes the current waitinglist-state as input, and returns the required updates to reach the desired state
export function nachrueckLogic(
  workshop: NarrowedEntityData<
    Workshop,
    {
      _id: 1;
      _class: 1;
      institutionID: 1;
      leftCapacity: 1;
      hasPrice: 1;
      isCanceled: 1;
      nachrueckBlockTimeForLowPriorityInDays: 1;
      requireManualAdmission: 1;
      settings: {
        autoNachrueckenEnabled: 1;
        blockAutomaticMoveUpDaysInAdvance: 1;
        blockActiveRegistrationHoursInAdvance: 1;
      };
      appointments: { _id: 1; start: 1; end: 1 };
      prices: { _id: 1; unitAmount: 1 };
    }
  >,
  waitingParticipations: _NL_PARTICIPATION[],
  options: { releasePass?: boolean } = {},
): WartelistenUpdate {
  logger.info({ workshopID: workshop._id }, 'executing nachrueckLogic');
  const wartelistenUpdate: WartelistenUpdate = {};
  const participationsWithoutAutoNachruecken = [] as _NL_PARTICIPATION[];

  const {
    canManuallyNachruecken,
    canAutoNachruecken,
    stillOnWaitinglist: _stillOnWaitinglist,
  } = getNachruecker(
    workshop,
    waitingParticipations.map(p => ({ ...p, product: workshop })),
    options,
  );

  for (const participation of waitingParticipations) {
    const update: WartelistenUpdate['string'] = {
      canManuallyNachruecken: false,
      canAutoNachruecken: false,
    };

    // Mark the participants that can be manually or automatically moved up
    if (canManuallyNachruecken.includes(participation._id)) {
      update.canManuallyNachruecken = true;
    }

    if (canAutoNachruecken.includes(participation._id)) {
      update.canAutoNachruecken = true;
    }

    wartelistenUpdate[participation._id] = update;
    if (!update.canAutoNachruecken) participationsWithoutAutoNachruecken.push(participation);
  }

  if (participationsWithoutAutoNachruecken.length === 0) return wartelistenUpdate;

  // sort
  participationsWithoutAutoNachruecken.sort(waitinglistSortLogic);

  // Calculate new waitingListPlaces
  for (let place = 1; place <= participationsWithoutAutoNachruecken.length; place++) {
    const participation = participationsWithoutAutoNachruecken[place - 1];
    if (!participation) throw new Error('unreachable');

    // Get the waitingListPlace safely
    const oldPlace = participation.waitingListPlace !== undefined ? participation.waitingListPlace : null;

    // Make sure participation._id exists and is valid
    if (!participation._id) {
      logger.error('Participation is missing an ID in nachrueckLogic');
      continue;
    }

    // Update the place in our update object
    if (!wartelistenUpdate[participation._id]) {
      throw new Error('Unreachable: every participation should have an update entry');
    }
    wartelistenUpdate[participation._id]!.place = {
      old: oldPlace,
      now: place,
    };
  }

  return wartelistenUpdate;
}

/**
 * Commits the waiting list updates to the MongoDB database
 * @param workshopID The ID of the workshop
 * @param wartelistenUpdate The updates to commit
 */
export async function commitWartelistenUpdates(
  workshopID: string,
  wartelistenUpdate: WartelistenUpdate,
): Promise<void> {
  logger.info({ workshopID }, 'commitWartelistenUpdates');
  const participationCol = mongo().collection<EntityData<Participation>>(Participation._collectionName);

  // Clean stale places even when no one is waiting and there are no place updates to commit.
  await participationCol.updateMany(
    {
      productID: workshopID,
      state: { $ne: ParticipationState.waiting },
      waitingListPlace: { $type: 'number' },
    } as any,
    { $set: { waitingListPlace: null } },
  );

  // Extract participation IDs from the update object
  const participationIDs = Object.keys(wartelistenUpdate);

  if (participationIDs.length === 0) return;

  // Process auto nachrucken - move participants up from waiting list
  const participationsToMoveUp = participationIDs.filter(
    id => wartelistenUpdate[id] && wartelistenUpdate[id].canAutoNachruecken,
  );

  for (const participationID of participationsToMoveUp) {
    try {
      await moveUpFromWaitinglistIntoParticipating(participationID, null)({});
    } catch (err) {
      logger.warn({ err, participationID }, 'moveUpFromWaitinglistIntoParticipating');
    }
  }

  // place updates
  const participationUpdates = participationIDs
    .map(participationID => {
      const update = wartelistenUpdate[participationID];
      if (!update) throw new Error('Unreachable');
      if (!update.place) return null;

      return {
        updateOne: {
          filter: { _id: participationID },
          update: {
            $set: {
              // Deliberately do NOT bump `updatedAt`: the waiting-list place is recomputed
              // automatically (change streams / nightly cascade), not changed by the person or an
              // admin. Bumping it made the "letzte Änderung am" column show the cron time instead.
              waitingListPlace: update.place.now,
            },
          },
        },
      };
    })
    .filter((update): update is NonNullable<typeof update> => update !== null);

  if (participationUpdates.length > 0) {
    await participationCol.bulkWrite([
      // Set all waitinglist places to null to avoid duplicate-key errors when swapping waitingListPlaces
      {
        updateMany: {
          filter: { _id: { $in: participationIDs } },
          update: { $set: { waitingListPlace: null } },
        },
      },
      ...participationUpdates,
    ]);
  }
}

const NOTIFICATION_CONCURRENCY = 10;
// Deliberate UX throttle: notify position N after N * stagger ms; admins can react between sends.
const VORRUECK_NOTIFICATION_STAGGER_MS = 2000;

export async function informUsersAboutNachrueckChanges(
  wartelistenUpdate: WartelistenUpdate,
  // Exact recipients of the "Nachrückplatz verfügbar" mail, already deduped per participant by the
  // caller's edge-trigger. The waitlist position ("Vorrück") mails are evaluated independently below.
  nachrueckplatzVerfuegbarParticipationIDs: string[] = [],
): Promise<void> {
  logger.info({ count: Object.keys(wartelistenUpdate).length }, 'informUsersAboutNachrueckChanges');
  const participationIDs = Object.keys(wartelistenUpdate);

  const manualResults = await runWithConcurrency(
    nachrueckplatzVerfuegbarParticipationIDs,
    NOTIFICATION_CONCURRENCY,
    async participationID => {
      await notifyNachrueckplatzVerfuegbar([participationID]);
      return participationID;
    },
  );
  const participationCol = mongo().collection<EntityData<Participation>>(Participation._collectionName);
  for (let i = 0; i < manualResults.length; i++) {
    const r = manualResults[i];
    if (r && !r.ok) {
      logger.warn(
        { err: r.error, participationID: nachrueckplatzVerfuegbarParticipationIDs[i] },
        'notifyNachrueckplatzVerfuegbar',
      );
      await participationCol.updateOne(
        { _id: nachrueckplatzVerfuegbarParticipationIDs[i] },
        { $set: { nachrueckplatzVerfuegbarNotified: false } },
      );
    }
  }

  const waitingListParticipations = participationIDs.filter(
    id => wartelistenUpdate[id] && !wartelistenUpdate[id].canAutoNachruecken,
  );

  // Fire-and-forget: this runs inside a change-stream handler, blocking would stall subsequent events.
  const sessionID = serverContext.sessionID;
  let place = 1;
  for (const participationID of waitingListParticipations) {
    const update = wartelistenUpdate[participationID];
    if (!update) {
      logger.error({ participationID }, 'Update object not found for participationID during notification');
      continue;
    }

    // When oldWaitinglistPlace is null, this is likely a new participant, so they already got the register mail
    if (update.place?.old !== update.place?.now) {
      const scheduleAt = place * VORRUECK_NOTIFICATION_STAGGER_MS;
      setTimeout(() => {
        void runInContext(sessionID, async () => {
          try {
            await notifyWartelisteVorrueck(participationID);
          } catch (err) {
            logger.error({ err, participationID }, 'could not notifyWartelisteVorrueck');
          }
        });
      }, scheduleAt);
    }
    place++;
  }
}

/**
 * Subject to collect workshop IDs that need waitinglist recalculation
 */
const workshopNachrueckSubject = new Subject<string>();

/**
 * Force nachrueck logic to be executed immediately for a specific workshop
 * This is useful when admins manually change participation states and need immediate feedback
 * @param workshopID The workshop ID to process
 */
export async function forceNachrueckLogic(workshopID: string): Promise<void> {
  logger.info({ workshopID }, 'Force executing nachrueck logic immediately');
  await processNachrueckForWorkshop(workshopID);
}

/**
 * Setup change stream listeners for automatic waitinglist monitoring
 * This consolidates all waitinglist updates into one place using MongoDB change streams
 */
export async function setupWaitinglistChangeStreamListeners() {
  logger.info('Setting up waitinglist change stream listeners');

  // Coalesce bursts of change-stream events for the same workshop into a single recalc (a bulk admin
  // action, or this function's own writes echoing back through the streams, can fire many events).
  const debouncedWorkshopIDs$ = workshopNachrueckSubject.pipe(
    bufferTime(15 * 1000),
    filter(workshopIDs => workshopIDs.length > 0),
    map(workshopIDs => [...new Set(workshopIDs)]),
  );

  debouncedWorkshopIDs$.subscribe(async workshopIDs => {
    logger.info({ count: workshopIDs.length }, 'Processing batched nachrueck updates');

    for (const workshopID of workshopIDs) {
      try {
        await processNachrueckForWorkshop(workshopID);
      } catch (err) {
        logger.error({ err, workshopID }, 'Error processing nachrueck for workshop');
      }
    }
  });

  // Setup Participation change stream
  await setupParticipationChangeStream();

  // Setup Workshop capacity change stream
  await setupWorkshopChangeStream();

  // Setup Product Settings change stream
  await setupProductSettingsChangeStream();

  logger.info('Waitinglist change stream listeners setup complete');
}

/**
 * Process nachrueck logic for a single workshop
 */
export async function processNachrueckForWorkshop(workshopID: string, options: { releasePass?: boolean } = {}) {
  logger.info({ workshopID }, 'Processing nachrueck logic for workshop');

  try {
    const _workshop = await loadRawEntityDataByIDOrFail(Workshop, workshopID, {
      _id: 1,
      _class: 1,
      institutionID: 1,
      hasPrice: 1,
      capacity: 1,
      isCanceled: 1,
      nachrueckBlockTimeForLowPriorityInDays: 1,
      requireManualAdmission: 1,
      settings: {
        autoNachrueckenEnabled: 1,
        blockAutomaticMoveUpDaysInAdvance: 1,
        inheritedBlockAutomaticMoveUpDaysInAdvance: 1,
        blockActiveRegistrationHoursInAdvance: 1,
      },
      appointments: { _id: 1, start: 1, end: 1 },
      prices: { _id: 1, unitAmount: 1 },
    });

    // Raw MongoDB load bypasses the Workshop entity resolver for nachrueckBlockTimeForLowPriorityInDays,
    // which falls back to the plugin default. Manually resolve it here.
    if (_workshop.nachrueckBlockTimeForLowPriorityInDays == null) {
      const priorityPlugin = getDbPluginForScope(
        'registration::product-waitinglist-priority-rules',
        _workshop.institutionID,
      );
      (_workshop as any).nachrueckBlockTimeForLowPriorityInDays = priorityPlugin.blockTimeForLowPriorityInDays;
    }

    const workshopSettings = await getMergedProductSettings(_workshop._id, [
      'autoNachrueckenEnabled',
      'blockAutomaticMoveUpDaysInAdvance',
      'blockActiveRegistrationHoursInAdvance',
    ]);

    const leftCapacity = await calculateWorkshopLeftCapacity(_workshop);

    const workshop = { ..._workshop, settings: workshopSettings, leftCapacity };
    const _waitingParticipations = await loadRawEntityDataByQuery(Participation, {
      productID: workshopID,
      state: ParticipationState.waiting,
    });
    const waitingParticipations = await Promise.all(
      _waitingParticipations.map(async participation => {
        const productPriceForUser = await getProductPriceForUser(participation.productID, participation.userID);
        return { ...participation, productPriceForUser };
      }),
    );
    const wartelistenUpdate = nachrueckLogic(workshop, waitingParticipations, options);
    await commitWartelistenUpdates(workshop._id, wartelistenUpdate);

    // "Nachrückplatz verfügbar" mail. The window is open while there are FREE MANUAL spots — the spots
    // left after auto-Nachrücken takes its share (with auto off, that's just leftCapacity). Dedup is
    // PER PARTICIPANT, not per workshop: each waitlister is notified once while the window stays open,
    // and the guard resets once the window closes. So a TN who joins the list while a spot is already
    // open still gets the mail, nobody is notified twice, and — because the window is keyed on the spot
    // count — an admin admission only fills spots and can never re-trigger it.
    const autoNachrueckCount = Object.values(wartelistenUpdate).filter(update => update.canAutoNachruecken).length;
    const manualFreeSpots = Math.max(workshop.leftCapacity - autoNachrueckCount, 0);
    const windowOpen = manualFreeSpots > 0;

    // Candidates = people still on the waiting list (auto-Nachrücker get moved straight into the spot).
    // Exclude low-priority participants, who only actively take spots after their block period, and
    // `entry_requirements_not_met`, who can never take the spot.
    const stillWaiting = waitingParticipations.filter(
      p =>
        !wartelistenUpdate[p._id]?.canAutoNachruecken &&
        p.nachrueckPriority !== 'entry_requirements_not_met' &&
        p.nachrueckPriority !== 'low',
    );
    const candidateIDs = windowOpen
      ? stillWaiting.filter(p => !p.nachrueckplatzVerfuegbarNotified).map(p => p._id)
      : [];

    const participationCol = mongo().collection<EntityData<Participation>>(Participation._collectionName);

    // Atomically CLAIM each candidate before mailing. A plain read-then-mark would race: the force path
    // (sync, inside the admin mutation) and the debounced change-stream flush can both read the same
    // waitlister as un-notified and both send. The conditional updateOne flips the flag only if it is
    // still un-set, so exactly one run gets modifiedCount === 1 and mails that TN. (No updatedAt bump:
    // this flag is internal bookkeeping, isn't materialized, and would otherwise churn downstream
    // dataview/materialized-view subscribers and shift the updatedAt-derived signoffDate resolver.)
    const toNotify: string[] = [];
    for (const id of candidateIDs) {
      const claim = await participationCol.updateOne(
        { _id: id, nachrueckplatzVerfuegbarNotified: { $ne: true } },
        { $set: { nachrueckplatzVerfuegbarNotified: true } },
      );
      if (claim.modifiedCount === 1) toNotify.push(id);
    }

    // Decision trace: makes "why was/wasn't the Nachrückplatz-verfügbar mail sent" answerable from logs.
    logger.info(
      {
        workshopID,
        leftCapacity: workshop.leftCapacity,
        autoNachrueckCount,
        manualFreeSpots,
        windowOpen,
        waitingCount: waitingParticipations.length,
        candidateCount: candidateIDs.length,
        notifyCount: toNotify.length,
      },
      'Nachrückplatz-verfügbar mail decision',
    );

    if (!windowOpen) {
      // Window closed → clear the per-participant guard for the WHOLE workshop (any state), so the next
      // opening notifies everyone fresh. This also covers a TN who was 'in' when the window closed and
      // later returns to the waiting list, so they don't carry a stale "already notified" flag.
      await participationCol.updateMany(
        { productID: workshopID, nachrueckplatzVerfuegbarNotified: true },
        { $set: { nachrueckplatzVerfuegbarNotified: false } },
      );
    }

    await informUsersAboutNachrueckChanges(wartelistenUpdate, toNotify);
  } catch (err) {
    logger.error({ err, workshopID }, 'Error in processNachrueckForWorkshop');
    throw err;
  }
}

/**
 * Setup change stream for Participation changes
 * Triggers when:
 * - New participation is created (insert)
 * - Participation state changes (update)
 * - Participation is deleted (delete)
 * - nachrueckPriority changes (update)
 */
function setupParticipationChangeStream() {
  const collection = mongo().collection(Participation._collectionName);
  const changeStream = collection.raw.watch(
    [
      {
        $match: {
          $or: [{ 'fullDocument._class': ParticipationWorkshop._class }],
        },
      },
    ],
    { fullDocument: 'updateLookup' },
  );

  changeStream.on('change', async change => {
    try {
      logger.debug({ change }, 'Participation change stream event');

      const operationType = change.operationType;
      let workshopID: string | null = null;
      let shouldTrigger = false;

      const updatedFields =
        operationType === 'update' ? Object.keys(change.updateDescription.updatedFields ?? {}) : null;
      if (operationType === 'insert' && change.fullDocument) {
        // New participation created
        workshopID = change.fullDocument.productID;
        shouldTrigger = true;
        logger.info({ participationID: change.documentKey._id, workshopID }, 'New participation detected');
      } else if (
        operationType === 'update' &&
        change.fullDocument &&
        (updatedFields?.includes('state') ||
          updatedFields?.includes('nachrueckPriority') ||
          updatedFields?.includes('fulfilledParticipationRequirements') ||
          updatedFields?.includes('appointmentParticipations'))
      ) {
        // Participation updated - check if relevant fields changed
        const newDoc = change.fullDocument;

        workshopID = newDoc.productID;

        shouldTrigger = true;
        logger.info(
          { participationID: change.documentKey._id, newState: newDoc.state, workshopID },
          'Participation changed',
        );
      } else if (operationType === 'delete' && change.fullDocumentBeforeChange) {
        // Participation deleted
        workshopID = change.fullDocumentBeforeChange.productID;
        shouldTrigger = true;
        logger.info({ participationID: change.documentKey._id, workshopID }, 'Participation deleted');
      } else {
        logger.debug({ change }, 'Participation change stream event skipped');
      }

      if (shouldTrigger && workshopID) {
        workshopNachrueckSubject.next(workshopID);
      }
    } catch (error) {
      logger.error({ error, change }, 'Error processing Participation change stream event');
    }
  });

  changeStream.on('error', error => {
    logger.error({ error }, 'Participation change stream error');
  });

  logger.info('Participation change stream listener started');
}

/**
 * Setup change stream for Workshop capacity changes
 * Triggers when workshop capacity is updated
 */
function setupWorkshopChangeStream() {
  const collection = mongo().collection(Workshop._collectionName);
  const changeStream = collection.raw.watch(
    [
      {
        $match: {
          $or: [{ 'fullDocument._class': Workshop._class }],
        },
      },
    ],
    { fullDocument: 'updateLookup' },
  );

  changeStream.on('change', async change => {
    try {
      logger.debug({ change }, 'Workshop change stream event');

      if (change.operationType === 'update' && change.fullDocument) {
        const newDoc = change.fullDocument;
        const workshopID = newDoc._id;
        logger.info({ workshopID, newDoc }, 'Workshop changed');
        workshopNachrueckSubject.next(workshopID);
      }
    } catch (error) {
      logger.error({ error, change }, 'Error processing Workshop change stream event');
    }
  });

  changeStream.on('error', error => {
    logger.error({ error }, 'Workshop change stream error');
  });

  logger.info('Workshop change stream listener started');
}

/**
 * Setup change stream for Product Settings changes
 * Triggers when settings that affect waiting list behavior are updated
 */
function setupProductSettingsChangeStream() {
  const collection = mongo().collection(Setting._collectionName);
  const changeStream = collection.raw.watch(
    [
      {
        $match: {
          $or: [{ 'fullDocument._class': Setting._class, 'fullDocument.productID': { $exists: true } }],
        },
      },
    ],
    { fullDocument: 'updateLookup' },
  );

  changeStream.on('change', async change => {
    try {
      logger.debug({ change }, 'Product Settings change stream event');

      if (change.operationType === 'update' && change.fullDocument) {
        const newDoc = change.fullDocument;
        const productID = newDoc.productID;

        if (productID) {
          logger.info({ productID, settingID: newDoc._id }, 'Product Settings changed');
          workshopNachrueckSubject.next(productID);
        }
      }
    } catch (error) {
      logger.error({ error, change }, 'Error processing Product Settings change stream event');
    }
  });

  changeStream.on('error', error => {
    logger.error({ error }, 'Product Settings change stream error');
  });

  logger.info('Product Settings change stream listener started');
}
